import { contentKindTitleLabel, planItemId, playbackClass, } from "@healthos/shared";
export const EMPTY_LIBRARY_RATING_SUMMARY = {
    upCount: 0,
    downCount: 0,
    totalCount: 0,
    approvalPercent: null,
    viewerVote: null,
};
/**
 * The API returns the eligible CMS catalog directly. Completion state remains
 * member-specific and is joined by the API.
 */
export function buildLibraryDisplayItems(items) {
    return items.map(libraryItemToDisplayItem);
}
export function libraryItemToContentItem(item) {
    const kind = item.kind === "Story" ? "Article" : item.kind;
    const metadata = item.metadata;
    const mediaUrl = metadataString(metadata, "videoUrl") ?? metadataString(metadata, "mediaUrl");
    const tags = Array.isArray(metadata.tags)
        ? metadata.tags.filter((tag) => typeof tag === "string")
        : [];
    const when = typeof metadata.when === "string" ? metadata.when : "Explore";
    const why = typeof metadata.why === "string" && metadata.why.trim().length > 0
        ? metadata.why
        : item.summary;
    return {
        id: planItemId(kind, item.contentId),
        kind,
        poster: item.imageUrl,
        title: item.title,
        when,
        why,
        dietaryLabels: item.dietaryLabels ?? [],
        healthBehaviours: item.healthBehaviours ?? [],
        resetMedia: playbackClass(kind) === "video"
            ? {
                description: item.summary ?? "",
                durationSeconds: typeof metadata.durationSeconds === "number" ? metadata.durationSeconds : null,
                videoUrl: mediaUrl,
            }
            : null,
        articleMedia: kind === "Article"
            ? {
                summary: item.summary ?? "",
                estimatedReadSeconds: typeof metadata.estimatedReadSeconds === "number"
                    ? metadata.estimatedReadSeconds
                    : null,
                byline: typeof metadata.byline === "string" ? metadata.byline : null,
            }
            : null,
        roleModelMedia: kind === "RoleModel"
            ? {
                summary: item.summary ?? "",
                estimatedReadSeconds: typeof metadata.estimatedReadSeconds === "number"
                    ? metadata.estimatedReadSeconds
                    : null,
                byline: typeof metadata.byline === "string" ? metadata.byline : null,
            }
            : null,
        recipeMedia: kind === "Recipe"
            ? {
                summary: item.summary ?? "",
                estimatedCookSeconds: typeof metadata.estimatedCookSeconds === "number"
                    ? metadata.estimatedCookSeconds
                    : null,
                tags,
            }
            : null,
        podcastMedia: playbackClass(kind) === "audio"
            ? {
                summary: item.summary ?? "",
                audioUrl: typeof metadata.audioUrl === "string" ? metadata.audioUrl : null,
                durationSeconds: typeof metadata.durationSeconds === "number" ? metadata.durationSeconds : null,
                podcastTitle: typeof metadata.podcastTitle === "string" ? metadata.podcastTitle : null,
                episodeNumber: typeof metadata.episodeNumber === "number" ? metadata.episodeNumber : null,
                seasonNumber: typeof metadata.seasonNumber === "number" ? metadata.seasonNumber : null,
            }
            : null,
    };
}
export function resetPlayerPayloadFromLibraryItem(item) {
    if (playbackClass(item.contentItem.kind) !== "video")
        return null;
    const videoUrl = item.contentItem.resetMedia?.videoUrl;
    if (!videoUrl)
        return null;
    return {
        title: item.contentItem.title,
        description: item.contentItem.resetMedia?.description ?? item.contentItem.why ?? item.summary ?? "",
        duration: item.contentItem.kind === "Guide"
            ? (item.contentItem.resetMedia?.durationSeconds ?? 0)
            : (item.contentItem.resetMedia?.durationSeconds ?? 60),
        videoUrl,
        kind: item.contentItem.kind === "Guide" || item.contentItem.kind === "Meditation"
            ? item.contentItem.kind
            : "Reset",
    };
}
export function podcastPlayerPayloadFromContentItem(item) {
    if (playbackClass(item.kind) !== "audio")
        return null;
    const audioUrl = item.podcastMedia?.audioUrl;
    if (!audioUrl)
        return null;
    return {
        title: item.title,
        summary: item.podcastMedia?.summary ?? item.why ?? "",
        audioUrl,
        imageUrl: item.poster ?? null,
        // A soundscape or bedtime story has no show; its kind is the honest
        // second line on the lock screen and the mini player.
        podcastTitle: item.podcastMedia?.podcastTitle ??
            (item.kind === "PodcastEpisode" ? null : contentKindTitleLabel(item.kind)),
    };
}
/** The player payload for the episode a walk moment named. */
export function podcastPlayerPayloadFromMomentEpisode(episode) {
    return {
        title: episode.title,
        summary: episode.description,
        audioUrl: episode.audioUrl,
        imageUrl: episode.thumbnailUrl,
        podcastTitle: episode.showTitle,
    };
}
export function podcastPlayerPayloadFromLibraryItem(item) {
    const payload = podcastPlayerPayloadFromContentItem(item.contentItem);
    if (!payload)
        return null;
    return {
        ...payload,
        summary: payload.summary || item.summary || "",
        imageUrl: item.imageUrl ?? payload.imageUrl,
    };
}
export function buildRecommendedDisplayItems(items) {
    return items.map(recommendationToDisplayItem);
}
/** Copy catalog completion state onto Heartbeat picks of the same content. */
export function withRecommendedCompletions(recommended, libraryItems) {
    const keys = new Set(libraryItems.flatMap((item) => (item.completed ? contentIdLookupKeys(item.contentId) : [])));
    return recommended.map((item) => ({
        ...item,
        completed: contentIdLookupKeys(item.contentId).some((key) => keys.has(key)),
    }));
}
export function replaceLibraryItemRating(data, contentId, rating) {
    if (!data)
        return data;
    return {
        ...data,
        items: data.items.map((item) => (item.contentId === contentId ? { ...item, rating } : item)),
    };
}
export function replaceLibraryRecommendationRating(data, contentId, rating) {
    if (!data)
        return data;
    return {
        ...data,
        items: data.items.map((item) => (item.contentId === contentId ? { ...item, rating } : item)),
    };
}
function contentIdLookupKeys(contentId) {
    const bare = contentId.includes(":") ? (contentId.split(":").pop() ?? contentId) : contentId;
    return bare === contentId ? [contentId] : [contentId, bare];
}
function metadataString(metadata, key) {
    const value = metadata[key];
    return typeof value === "string" && value.trim() ? value : null;
}
function recommendationToDisplayItem(item) {
    const now = new Date().toISOString();
    const libraryItem = {
        id: `recommended:${item.contentId}`,
        contentId: item.contentId,
        kind: item.kind,
        title: item.title,
        summary: item.summary,
        imageUrl: item.imageUrl,
        metadata: item.metadata,
        dietaryLabels: item.dietaryLabels,
        healthBehaviours: item.healthBehaviours ?? [],
        addedAt: now,
        updatedAt: now,
        completed: false,
        rating: item.rating ?? EMPTY_LIBRARY_RATING_SUMMARY,
    };
    return {
        source: "recommended",
        id: libraryItem.id,
        contentId: item.contentId,
        kind: item.kind,
        title: item.title,
        summary: item.summary,
        imageUrl: item.imageUrl,
        metadata: item.metadata,
        dietaryLabels: item.dietaryLabels,
        healthBehaviours: item.healthBehaviours ?? [],
        contentItem: libraryItemToContentItem(libraryItem),
        completed: false,
        rating: item.rating ?? EMPTY_LIBRARY_RATING_SUMMARY,
        libraryItem,
        addedAt: now,
        updatedAt: now,
    };
}
function libraryItemToDisplayItem(libraryItem) {
    return {
        source: "catalog",
        id: libraryItem.id,
        contentId: libraryItem.contentId,
        kind: libraryItem.kind,
        title: libraryItem.title,
        summary: libraryItem.summary,
        imageUrl: libraryItem.imageUrl,
        metadata: libraryItem.metadata,
        dietaryLabels: libraryItem.dietaryLabels ?? [],
        healthBehaviours: libraryItem.healthBehaviours ?? [],
        contentItem: libraryItemToContentItem(libraryItem),
        completed: libraryItem.completed === true,
        rating: libraryItem.rating ?? EMPTY_LIBRARY_RATING_SUMMARY,
        libraryItem,
        addedAt: libraryItem.addedAt,
        updatedAt: libraryItem.updatedAt,
    };
}
//# sourceMappingURL=library-projection.js.map